import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ReplaceModal from '../components/ReplaceModal';
import NoteEditor from '../components/NoteEditor';
import './ChecklistPage.css';

export default function ChecklistPage() {
  const { checklistId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [checklist, setChecklist] = useState(null);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [addItemTarget, setAddItemTarget] = useState(null);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);
  const [isConnected, setIsConnected] = useState(true);
  const socketRef = useRef(null);

  const canOperate = user?.role === 'admin' || user?.role === 'deposito';

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/checklists/${checklistId}`);
      setChecklist(data);
      setItems(data.items || []);
      setNotes(data.notes || []);
    } catch (e) {
      if (e.response?.status === 404) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [checklistId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Socket.io
  useEffect(() => {
    const socket = io('/', { withCredentials: true });
    socketRef.current = socket;

    socket.emit('checklist:join', checklistId);

    socket.on('item:updated', ({ item }) => {
      setItems(prev => prev.map(i => i.id === item.id ? item : i));
    });

    socket.on('note:updated', ({ note }) => {
      console.log('note:updated recibido', note);
      setNotes(prev => {
        const exists = prev.find(n => n.id === note.id);
        return exists
          ? prev.map(n => n.id === note.id ? note : n)
          : [...prev, note];
      });
    });

    socket.on('note:deleted', ({ zone, category }) => {
      setNotes(prev => prev.filter(n =>
        !((n.zone || null) === (zone || null) && (n.category || null) === (category || null))
      ));
    });

    socket.on('checklist:status', ({ status }) => {
      setChecklist(prev => ({ ...prev, status }));
    });

    socket.on('item:added', ({ item }) => {
      setItems(prev => [...prev, item]);
    });

    socket.on('item:deleted', ({ itemId }) => {
      setItems(prev => prev.filter(i => i.id !== itemId));
    });

    socket.on('checklist:reset', () => {
      load();
    });

    socket.on('disconnect', () => setIsConnected(false));
    socket.on('connect', () => setIsConnected(true));

    return () => {
      socket.emit('checklist:leave', checklistId);
      socket.disconnect();
    };
  }, [checklistId]);

  const toggleItem = async (item) => {
    if (!canOperate || !isConnected) return;
    const newChecked = !item.is_checked;
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_checked: newChecked } : i));
    try {
      await api.patch(`/checklists/${checklistId}/items/${item.id}`, {
        is_checked: newChecked
      });
    } catch (e) {
      console.error(e);
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_checked: item.is_checked } : i));
    }
  };

  const handleReplace = async (item, newName) => {
    try {
      await api.patch(`/checklists/${checklistId}/items/${item.id}`, {
        current_name: newName
      });
      setItems(prev => prev.map(i =>
        i.id === item.id ? { ...i, current_name: newName, is_replaced: newName !== i.original_name } : i
      ));
      setReplaceTarget(null);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleNote = async (zone, category, note) => {
    try {
      if (note === '') {
        await api.delete(`/checklists/${checklistId}/notes`, { data: { zone, category } });
        setNotes(prev => prev.filter(n =>
          !((n.zone || null) === (zone || null) && (n.category || null) === (category || null))
        ));
      } else {
        const res = await api.put(`/checklists/${checklistId}/notes`, { zone, category, note });
        setNotes(prev => {
          const exists = prev.find(n =>
            (n.zone || null) === (zone || null) && (n.category || null) === (category || null)
          );
          return exists
            ? prev.map(n =>
                (n.zone || null) === (zone || null) && (n.category || null) === (category || null)
                  ? res.data : n
              )
            : [...prev, res.data];
        });
      }
      setNoteTarget(null);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDeleteNote = async (zone, category) => {
    try {
      await api.delete(`/checklists/${checklistId}/notes`, { data: { zone, category } });
      setNotes(prev => prev.filter(n =>
        !((n.zone || null) === (zone || null) && (n.category || null) === (category || null))
      ));
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleAddItem = async (zone, category, name) => {
    try {
      await api.post(`/checklists/${checklistId}/items`, { zone, category, name });
      setAddItemTarget(null);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDeleteItem = (itemId, itemName) => {
    setDeleteItemTarget({ id: itemId, name: itemName });
  };

  const confirmDeleteItem = async () => {
    if (!deleteItemTarget) return;
    try {
      await api.delete(`/checklists/${checklistId}/items/${deleteItemTarget.id}`);
      setItems(prev => prev.filter(i => i.id !== deleteItemTarget.id));
      setDeleteItemTarget(null);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
      setDeleteItemTarget(null);
    }
  };

  const handleReset = async () => {
    try {
      await api.post(`/checklists/${checklistId}/reset`);
      load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const toggleStatus = async () => {
    const newStatus = checklist.status === 'open' ? 'closed' : 'open';
    try {
      await api.patch(`/checklists/${checklistId}/status`, { status: newStatus });
      setChecklist(prev => ({ ...prev, status: newStatus }));
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  // Group items by zone + category
  const grouped = groupItems(items);

  const checkedCount = items.filter(i => i.is_checked).length;
  const progress = items.length > 0 ? Math.round((checkedCount / items.length) * 100) : 0;

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!checklist) return null;

  return (
    <div className="checklist-page">
      {!isConnected && (
        <div className="cl-disconnected-banner">
          Sin conexión — los cambios no se guardarán
        </div>
      )}
      {/* Header */}
      <div className="cl-header">
        <button onClick={() => navigate('/')} className="btn btn-ghost btn-sm">
          ← Volver
        </button>

        <div className="cl-title-block">
          <div className="cl-event-name">{checklist.event_name}</div>
          <div className="cl-meta">
            <span className={`badge ${checklist.type === 'CARGA' ? 'badge-blue' : 'badge-green'}`}>
              {checklist.type === 'CARGA' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              )}
              {checklist.type}
            </span>
            <span className={`badge ${checklist.status === 'open' ? 'badge-green' : 'badge-gray'}`}>
              {checklist.status === 'open' ? '● Activo' : '🔒 Cerrado'}
            </span>
          </div>
        </div>

        {canOperate && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShowResetConfirm(true)}
              className="btn btn-ghost btn-sm"
            >
              Reiniciar
            </button>
            <button
              onClick={toggleStatus}
              className={`btn btn-sm ${checklist.status === 'open' ? 'btn-ghost' : 'btn-primary'}`}
            >
              {checklist.status === 'open' ? 'Cerrar checklist' : 'Reabrir'}
            </button>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="cl-progress">
        <div className="cl-progress-bar">
          <div className="cl-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="cl-progress-label">
          {checkedCount} / {items.length} ítems ({progress}%)
        </span>
      </div>

      {/* Groups */}
      <div className="cl-groups">
        {grouped.map((zoneGroup, zi) => (
          <div key={zi} className="cl-zone-block">
            <div className="cl-zone-title">
              {zoneGroup.zone || 'GENERAL'}
            </div>
            <div className="cl-categories">
              {zoneGroup.categories.map((catGroup, ci) => {
                const note = notes.find(n =>
                  (n.zone || null) === (zoneGroup.zone || null) &&
                  (n.category || null) === (catGroup.category || null)
                );
                return (
                  <div
                    key={ci}
                    className={`cl-category-block${ci < zoneGroup.categories.length - 1 ? ' cl-category-sep' : ''}`}
                  >
                    <div className="cl-category-header">
                      <span className="cl-category-title">
                        {catGroup.category || 'General'}
                      </span>
                      <div className="cl-group-actions">
                        <span className="cl-group-count text-dim text-xs">
                          {catGroup.items.filter(i => i.is_checked).length}/{catGroup.items.length}
                        </span>
                        {canOperate && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: 12, padding: '4px 10px' }}
                            onClick={() => setAddItemTarget({ zone: zoneGroup.zone, category: catGroup.category })}
                          >
                            + Equipo
                          </button>
                        )}
                        {canOperate && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: 12, padding: '4px 10px' }}
                            onClick={() => setNoteTarget({ zone: zoneGroup.zone, category: catGroup.category, existing: note })}
                          >
                            {note ? '📝 Nota' : '+ Nota'}
                          </button>
                        )}
                      </div>
                    </div>
                    {note && (
                      <div
                        className="cl-note"
                        onClick={() => canOperate && setNoteTarget({ zone: zoneGroup.zone, category: catGroup.category, existing: note })}
                      >
                        📝 {note.note}
                      </div>
                    )}
                    <div className="cl-items">
                      {catGroup.items.map(item => (
                        <CheckItem
                          key={item.id}
                          item={item}
                          canOperate={canOperate}
                          isCarga={checklist.type === 'CARGA'}
                          onToggle={() => toggleItem(item)}
                          onReplace={() => canOperate && setReplaceTarget(item)}
                          onDelete={handleDeleteItem}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Replace modal */}
      {replaceTarget && (
        <ReplaceModal
          item={replaceTarget}
          onConfirm={(newName) => handleReplace(replaceTarget, newName)}
          onClose={() => setReplaceTarget(null)}
        />
      )}

      {/* Note editor */}
      {noteTarget && (
        <NoteEditor
          zone={noteTarget.zone}
          category={noteTarget.category}
          existing={noteTarget.existing}
          onSave={handleNote}
          onClose={() => setNoteTarget(null)}
          onDelete={handleDeleteNote}
        />
      )}

      {/* Add item modal */}
      {addItemTarget && (
        <div className="modal-overlay" onClick={() => setAddItemTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">
              Agregar equipo — {addItemTarget.category || addItemTarget.zone || 'General'}
            </h3>
            <AddItemForm
              onConfirm={(name) => handleAddItem(addItemTarget.zone, addItemTarget.category, name)}
              onClose={() => setAddItemTarget(null)}
            />
          </div>
        </div>
      )}

      {/* Delete item confirm modal */}
      {deleteItemTarget && (
        <div className="modal-overlay" onClick={() => setDeleteItemTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">¿Borrar equipo?</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: '14px', marginBottom: '8px' }}>
              Vas a borrar:
            </p>
            <p style={{ color: 'var(--yellow)', fontWeight: 600, marginBottom: '24px' }}>
              {deleteItemTarget.name}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteItemTarget(null)}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteItem}>
                Borrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset confirm modal */}
      {showResetConfirm && (
        <div className="modal-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">¿Reiniciar checklist?</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: '14px', marginBottom: '24px' }}>
              Se borrarán todos los checks, reemplazos y notas. Esta acción no se puede deshacer.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-ghost"
                onClick={() => setShowResetConfirm(false)}
              >
                Cancelar
              </button>
              <button
                className="btn btn-danger"
                onClick={async () => {
                  setShowResetConfirm(false);
                  await handleReset();
                }}
              >
                Sí, reiniciar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckItem({ item, canOperate, isCarga, onToggle, onReplace, onDelete }) {
  return (
    <div className={`cl-item ${item.is_checked ? 'checked' : ''} ${item.is_replaced ? 'replaced' : ''}`}>
      <button
        className={`cl-checkbox ${item.is_checked ? 'checked' : ''}`}
        onClick={onToggle}
        disabled={!canOperate}
      >
        {item.is_checked && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="20,6 9,17 4,12"/>
          </svg>
        )}
      </button>

      <div className="cl-item-content" onClick={onReplace}>
        <div className="cl-item-top">
          <span className={item.is_replaced ? 'replaced-name' : 'item-name'}>
            {item.current_name}
          </span>
          {item.is_checked && item.checked_by_name && (
            <span className="cl-item-by text-mute text-xs">{item.checked_by_name}</span>
          )}
        </div>
        {item.is_replaced && !item.is_manual && (
          <span className="original-name">
            ↩ {item.original_name}
          </span>
        )}
      </div>

      {item.is_manual && isCarga && canOperate && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(item.id, item.current_name); }}
          className="btn btn-icon btn-ghost"
          style={{ color: 'var(--red)', fontSize: 14, padding: '2px 6px', flexShrink: 0 }}
          title="Borrar equipo"
        >
          ✕
        </button>
      )}
    </div>
  );
}

function AddItemForm({ onConfirm, onClose }) {
  const [value, setValue] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (value.trim()) onConfirm(value.trim()); }}>
      <div className="form-group">
        <label className="form-label">Nombre del equipo</label>
        <input
          autoFocus
          className="input"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="Ej: Cable HDMI 5m"
        />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
        <button type="submit" className="btn btn-primary" disabled={!value.trim()}>
          Agregar
        </button>
      </div>
    </form>
  );
}

function groupItems(items) {
  const zones = [];
  const zoneIndex = new Map();

  for (const item of items) {
    const zoneKey = item.zone || '';
    if (!zoneIndex.has(zoneKey)) {
      zoneIndex.set(zoneKey, zones.length);
      zones.push({ zone: item.zone || null, categories: [] });
    }
    const zoneEntry = zones[zoneIndex.get(zoneKey)];

    const catKey = item.category || '';
    let catEntry = zoneEntry.categories.find(c => (c.category || '') === catKey);
    if (!catEntry) {
      catEntry = { category: item.category || null, items: [] };
      zoneEntry.categories.push(catEntry);
    }
    catEntry.items.push(item);
  }

  return zones;
}
